/**
 * Fulfillment modality chooser — Pickup | Shipping (DeliveryPolicy UX).
 * SHIPPING shows policy ETA/deadline/priceRange/warning/mechanics + address.
 * No method radios / payable charge (plan G9/G13).
 */
import { useTranslation } from 'react-i18next';
import {
  isDeliveryPolicyDisplayReady,
  resolveDeliveryPolicyBlob,
} from '../deliveryPolicyLocale.js';
import {
  isShippingModalityUxVisible,
  type CustomerDeliveryPolicyDTO,
  type EligibleShippingMethodsDTO,
} from '../shippingCheckoutApi.js';
import {
  EMPTY_SHIPPING_ADDRESS,
  isShippingAddressComplete,
  type ShippingAddressDraft,
} from '../shippingAddressTypes.js';
import { ShippingAddressFields } from './ShippingAddressFields.js';

export type FulfillmentModality = 'PICKUP' | 'SHIPPING';

export interface FulfillmentModalityPanelProps {
  readonly eligible: EligibleShippingMethodsDTO | null | undefined;
  readonly eligibleLoading?: boolean;
  readonly modality: FulfillmentModality;
  readonly onModalityChange: (mode: FulfillmentModality) => void;
  readonly address: ShippingAddressDraft;
  readonly onAddressChange: (next: ShippingAddressDraft) => void;
  readonly saveAddressToMembership?: boolean;
  readonly onSaveAddressToMembershipChange?: (next: boolean) => void;
}

export function FulfillmentModalityPanel({
  eligible,
  eligibleLoading = false,
  modality,
  onModalityChange,
  address,
  onAddressChange,
  saveAddressToMembership = false,
  onSaveAddressToMembershipChange,
}: FulfillmentModalityPanelProps): JSX.Element | null {
  const { t, i18n } = useTranslation('customer');
  const uiLanguage = i18n.resolvedLanguage ?? i18n.language;

  if (eligibleLoading) {
    return (
      <p className="m-0 text-sm text-[var(--color-on-surface-muted)]" data-testid="shipping-modality-loading">
        {t('checkout.shipping.loadingPolicy')}
      </p>
    );
  }

  if (!isShippingModalityUxVisible(eligible)) {
    return null;
  }

  const policy = eligible?.policy ?? null;
  const policyReady = isDeliveryPolicyDisplayReady(policy, uiLanguage);

  return (
    <section
      className="flex flex-col gap-3"
      data-testid="fulfillment-modality-panel"
      aria-label={t('checkout.shipping.modalityLegend')}
    >
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1 text-sm font-semibold">
          {t('checkout.shipping.modalityLegend')}
        </legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="fulfillment-modality"
            value="PICKUP"
            checked={modality === 'PICKUP'}
            onChange={() => onModalityChange('PICKUP')}
            data-testid="fulfillment-modality-pickup"
          />
          {t('checkout.shipping.modalityPickup')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="fulfillment-modality"
            value="SHIPPING"
            checked={modality === 'SHIPPING'}
            onChange={() => onModalityChange('SHIPPING')}
            data-testid="fulfillment-modality-shipping"
          />
          {t('checkout.shipping.modalityShipping')}
        </label>
      </fieldset>

      {modality === 'SHIPPING' ? (
        <div className="flex flex-col gap-3" data-testid="shipping-modality-fields">
          {policyReady && policy != null ? (
            <DeliveryPolicySummary policy={policy} uiLanguage={uiLanguage} />
          ) : (
            <p
              className="m-0 text-sm text-[var(--color-danger)]"
              role="alert"
              data-testid="shipping-policy-fail-closed"
            >
              {t('checkout.shipping.policyUnavailable')}
            </p>
          )}
          <ShippingAddressFields
            value={address}
            onChange={onAddressChange}
            saveAddressToMembership={saveAddressToMembership}
            onSaveAddressToMembershipChange={onSaveAddressToMembershipChange}
          />
        </div>
      ) : null}
    </section>
  );
}

function DeliveryPolicySummary({
  policy,
  uiLanguage,
}: {
  readonly policy: CustomerDeliveryPolicyDTO;
  readonly uiLanguage: string | undefined;
}): JSX.Element {
  const { t } = useTranslation('customer');
  const priceRange = resolveDeliveryPolicyBlob(policy.priceRangeDescription, uiLanguage) ?? '';
  const warning = resolveDeliveryPolicyBlob(policy.outOfBandFeeWarning, uiLanguage) ?? '';
  const mechanics = resolveDeliveryPolicyBlob(policy.mechanicsDescription, uiLanguage) ?? '';

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-3 text-sm"
      data-testid="shipping-delivery-policy"
    >
      <p className="m-0 font-semibold">{t('checkout.shipping.policyTitle')}</p>
      <p className="m-0" data-testid="shipping-policy-eta">
        {t('checkout.shipping.etaRange', {
          min: policy.etaMinBusinessDays,
          max: policy.etaMaxBusinessDays,
        })}
      </p>
      <p className="m-0" data-testid="shipping-policy-deadline">
        {t('checkout.shipping.deadlineBusinessDays', {
          days: policy.latestDeliveryBusinessDays,
        })}
      </p>
      <p className="m-0" data-testid="shipping-policy-price-range">
        {priceRange}
      </p>
      <p
        className="m-0 text-[var(--color-on-surface-muted)]"
        data-testid="shipping-policy-warning"
      >
        {warning}
      </p>
      <p className="m-0" data-testid="shipping-policy-mechanics">
        {mechanics}
      </p>
      <p
        className="m-0 text-[var(--color-on-surface-muted)]"
        data-testid="shipping-advance-promise"
      >
        {t('checkout.shipping.lateReportAdvancePromise')}
      </p>
    </div>
  );
}

export function isShippingModalityReady(input: {
  readonly modality: FulfillmentModality;
  readonly address: ShippingAddressDraft;
  readonly policy?: CustomerDeliveryPolicyDTO | null;
  /** Required for SHIPPING when policy is assessed — same locale as display (G15). */
  readonly uiLanguage?: string;
}): boolean {
  if (input.modality !== 'SHIPPING') {
    return true;
  }
  if (!isShippingAddressComplete(input.address)) {
    return false;
  }
  if (input.policy === undefined) {
    return true;
  }
  const uiLanguage = input.uiLanguage?.trim();
  if (uiLanguage == null || uiLanguage.length === 0) {
    // Fail-closed: ready gate must not ignore UI locale when policy is in play.
    return false;
  }
  return isDeliveryPolicyDisplayReady(input.policy, uiLanguage);
}

export { EMPTY_SHIPPING_ADDRESS };
